import { ErrorType } from "@/app/api/utils/api-response";
import { getAuthProfile } from "@/app/auth/actions";
import { requireStripeClient } from "@/lib/stripe";
import { createClient } from "@/utils/supabase/client";
import { cache } from "react";

export type SubscriptionPlan = {
  id: string; // Price ID
  meter: string | null;
  active: boolean;
  amount: number;
  object: "plan";
  created: number;
  product: string; // Product ID
  currency: string;
  interval: "month" | "year" | "day" | "week";
  livemode: boolean;
  metadata: Record<string, unknown>;
  nickname: string | null;
  tiers_mode: string | null;
  usage_type: string;
  amount_decimal: string;
  billing_scheme: string;
  interval_count: number;
  aggregate_usage: string | null;
  transform_usage: unknown;
  trial_period_days: number | null;
};

export type AuthSubscription = {
  id: string | null;
  plan: SubscriptionPlan | undefined;
  product: { id: string | null; name: string | null } | null;
  status: string;
  credits: number;
  renews_at: string | null;
  cancel_at: string | null;
  cancels_at: string | null;
  canceled_at: string | null;
};

export const getAuthSubscription = cache(async (): Promise<{
  subscription: AuthSubscription | null;
  error: Error | null;
}> => {
  const supabase = await createClient();

  const { data, error } = await supabase.functions.invoke<
    AuthSubscription
  >(
    "auth-subscription",
    {
      method: "GET",
    },
  );

  return {
    subscription: data ?? null,
    error,
  };
});

export type SubscriptionGuardResult =
  | {
    error: null;
    subscription: AuthSubscription;
  }
  | {
    subscription: null;
    error: { message: string; code: ErrorType };
  };

/** Ensures the user has an active subscription and credits. Use before credit-consuming actions. */
export async function subscriptionGuard(): Promise<SubscriptionGuardResult> {
  const { subscription, error } = await getAuthSubscription();

  if (error || !subscription || subscription.status !== "active") {
    return {
      subscription: null,
      error: {
        message: "No subscription found",
        code: ErrorType.NO_SUBSCRIPTION_FOUND,
      },
    };
  }

  if (subscription.credits <= 0) {
    return {
      subscription: null,
      error: {
        message: "No credits found",
        code: ErrorType.NO_CREDITS_FOUND,
      },
    };
  }

  return { error: null, subscription };
}

export async function getCreditBalance(): Promise<{
  credits: number;
  error: Error | null;
}> {
  const { profile, error } = await getAuthProfile();
  if (error || !profile) {
    return { credits: 0, error: error ?? new Error("Unauthorized") };
  }
  return {
    credits: typeof profile.credits === "number" ? profile.credits : 0,
    error: null,
  };
}

function getScheduleId(
  schedule: string | { id: string } | null | undefined,
): string | null {
  if (!schedule) return null;
  return typeof schedule === "string" ? schedule : schedule.id ?? null;
}

export async function cancelSubscription(subscriptionId: string): Promise<
  { error: string | null }
> {
  const { profile } = await getAuthProfile();
  if (!profile?.stripe_customer_id) {
    return { error: "No billing customer found." };
  }
  const stripe = requireStripeClient();
  try {
    const sub = await stripe.subscriptions.retrieve(subscriptionId, {
      expand: ["schedule"],
    });
    if (sub.customer !== profile.stripe_customer_id) {
      return { error: "Subscription does not belong to your account." };
    }
    const scheduleId = getScheduleId(
      sub.schedule as string | { id: string } | null,
    );
    if (scheduleId) {
      await stripe.subscriptionSchedules.release(scheduleId);
    }
    await stripe.subscriptions.update(subscriptionId, {
      cancel_at_period_end: true,
    });
    return { error: null };
  } catch (err) {
    const message = err instanceof Error
      ? err.message
      : "Failed to cancel subscription.";
    return { error: message };
  }
}

export async function reactivateSubscription(subscriptionId: string): Promise<
  { error: string | null }
> {
  const { profile } = await getAuthProfile();
  if (!profile?.stripe_customer_id) {
    return { error: "No billing customer found." };
  }
  const stripe = requireStripeClient();
  try {
    const sub = await stripe.subscriptions.retrieve(subscriptionId, {
      expand: ["schedule"],
    });
    if (sub.customer !== profile.stripe_customer_id) {
      return { error: "Subscription does not belong to your account." };
    }
    const scheduleId = getScheduleId(
      sub.schedule as string | { id: string } | null,
    );
    if (scheduleId) {
      await stripe.subscriptionSchedules.release(scheduleId);
    }
    await stripe.subscriptions.update(subscriptionId, {
      cancel_at_period_end: false,
    });
    return { error: null };
  } catch (err) {
    const message = err instanceof Error
      ? err.message
      : "Failed to reactivate subscription.";
    return { error: message };
  }
}

export type Payment = {
  id: string;
  currency: "usd";
  customer: string;
  period_end: string;
  period_start: string;
  status: string;
  subscription: string;
  total: number;
  created_at: string;
};

export async function getPaymentHistory(): Promise<Payment[]> {
  const supabase = await createClient();
  const { data, error } = await supabase.functions.invoke<Payment[]>(
    "payment-history",
  );

  if (error) {
    throw error?.message ?? new Error("Failed to get payment history");
  }

  return data ?? [];
}

export type Charge = {
  id: string;
  amount: number;
  currency: string;
  customer: string;
  description: string | null;
  invoice: string | null;
  payment_intent: string | null;
  status: string;
  created: string;
};

export async function getChargesHistory(): Promise<Charge[]> {
  const supabase = await createClient();
  const { data, error } = await supabase.functions.invoke<Charge[]>(
    "charges-history",
  );

  if (error) {
    throw error?.message ?? new Error("Failed to get charges history");
  }

  return data ?? [];

  // if (error) {
  //   throw error?.message ?? new Error("Failed to get charges history");
  // }

  // return data ?? [];
  // const profile = {
  //   stripe_customer_id: "cus_Tye96vfGOoOBcf",
  // };
  // if (!profile?.stripe_customer_id) {
  //   return [];
  // }
  // const stripe = requireStripeClient();
  // const { data: charges } = await stripe.charges.list({
  //   customer: profile.stripe_customer_id,
  //   limit: 50,
  // });

  // return (charges ?? []).map((
  //   ch: {
  //     id: string;
  //     created: number;
  //     amount: number;
  //     currency: string;
  //     status: string;
  //     description: string | null;
  //   },
  // ) => ({
  //   id: ch.id,
  //   created_at: ch.created,
  //   amount: ch.amount,
  //   currency: ch.currency ?? "usd",
  //   status: ch.status ?? "unknown",
  //   description: ch.description ?? null,
  // }));
}
